import { DatePipe, NgClass } from '@angular/common';
import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { environment } from '../../../../environments/environment';
import { Chart, registerables } from 'chart.js';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { FinancialDashboardService } from '../../../core/services/api-services/financial-dashboard.service';
import { FinancialDashboardSummary } from './models/financial-dashboard-summary';
import { FundUsageIndicator } from './models/fund-usage-indicator';
import { BreadcrumbComponent, BreadcrumbItem } from "../../../shared/components/breadcrumb/breadcrumb.component";
import { BreadcrumbService } from '../../../shared/components/breadcrumb/service/breadcrumb.service';
import { ToastrService } from 'ngx-toastr';
import { ContributorAccountDetailsComponent } from '../contributor-account-details/contributor-account-details.component';
import { ContributorAccountDetail } from '../contributor-account-details/contributor-account-detail.model';
import { ContributorAccountService } from '../../../core/services/api-services/contributor-account.service';

Chart.register(...registerables);

@Component({
  selector: 'app-financial-dashboard-chart',
  standalone: true,
  providers: [DatePipe],
  imports: [FormsModule, NgClass, BreadcrumbComponent, ContributorAccountDetailsComponent],
  templateUrl: './financial-dashboard-chart.component.html',
  styleUrl: './financial-dashboard-chart.component.css'
})
export class FinancialDashboardChartComponent implements OnInit, OnDestroy {

  breadcrumbItems: BreadcrumbItem[] = [];
  private destroy$ = new Subject<void>();

  @ViewChild('chartCanvas', { static: false }) chartCanvas!: ElementRef<HTMLCanvasElement>;

  readonly isProd = environment.production;

  chart: Chart | null = null;
  summary: FinancialDashboardSummary | null = null;
  fundUsageIndicators: FundUsageIndicator[] = [];
  error = '';
  loading = false;
  accountData: ContributorAccountDetail | null = null;

  // Date filter properties
  startDate: string = '';
  endDate: string = '';
  maxStartDate: string = '';
  minEndDate: string = '';
  currentYear: number = new Date().getFullYear();
  currentMonth: number = new Date().getMonth() + 1;

  dataSubscriber$!: Subscription;

  constructor(
    private financialDashboardService: FinancialDashboardService,
    private contributorAccountService: ContributorAccountService,
    private datePipe: DatePipe,
    private breadcrumbService: BreadcrumbService,
    private toastrService: ToastrService) {

    this.dataSubscriber$ = new Subscription();

    // Set default to current month
    const year = this.currentYear;
    const month = String(this.currentMonth).padStart(2, '0');
    this.startDate = `${year}-${month}-01`;
    this.endDate = this.getLastDayOfMonth(year, this.currentMonth);
  }

  ngOnInit(): void {
    // Subscribe to breadcrumb changes
    this.breadcrumbService.breadcrumbs$
      .pipe(takeUntil(this.destroy$))
      .subscribe(items => {
        this.breadcrumbItems = items;
      });

    // Set breadcrumbs for this page
    this.breadcrumbService.setBreadcrumbs([
      { label: 'Dashboard', url: '/dashboard', icon: 'bi bi-house-door' },
      { label: 'Fund Management', icon: 'bi bi-cash-stack' },
      { label: 'Summary', icon: 'bi bi-file-earmark-text' }
    ]);


    this.maxStartDate = new Date().toISOString().split('T')[0];
    this.loadAccountData();
  }

  private loadAccountData(): void {
    this.contributorAccountService.getDebitAccountBalance().subscribe({
      next: (response) => {
        if (response?.header?.status === 'success' && response.body?.length > 0) {
          this.accountData = response.body[0];
          return;
        }
        if (Array.isArray(response) && (response as any[]).length > 0) {
          this.accountData = (response as any[])[0];
          return;
        }
        const generic = response as any;
        if (generic?.success && Array.isArray(generic.data) && generic.data.length > 0) {
          this.accountData = generic.data[0];
        }
      },
      error: (err) => console.error('Failed to load debit account balance', err)
    });
  }

  ngAfterViewInit(): void {
    // Load data after view is ready
    this.loadDashboardData();
  }

  onStartDateChange(): void {
    // Reset end date when start date changes
    this.endDate = '';

    // Set minimum selectable end date to the day after start date
    if (this.startDate) {
      const startDateObj = new Date(this.startDate);
      startDateObj.setDate(startDateObj.getDate() + 1);
      this.minEndDate = startDateObj.toISOString().split('T')[0];
    } else {
      this.minEndDate = '';
    }
  }

  onFilterApply(): void {
    // Validate before applying filter
    if (!this.startDate || !this.endDate) {
      alert('Please select both start and end dates');
      return;
    }

    if (new Date(this.endDate) <= new Date(this.startDate)) {
      alert('End date must be after start date');
      return;
    }

    if (this.startDate && this.endDate) {
      this.loadDashboardData(this.startDate, this.endDate);
    }
  }

  onFilterReset(): void {
    const year = this.currentYear;
    const month = String(this.currentMonth).padStart(2, '0');
    this.startDate = `${year}-${month}-01`;
    this.endDate = this.getLastDayOfMonth(year, this.currentMonth);
    this.loadDashboardData();
  }

  private getLastDayOfMonth(year: number, month: number): string {
    const lastDay = new Date(year, month, 0).getDate();
    return `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
  }

  loadDashboardData(startDate?: string, endDate?: string): void {
    this.error = '';
    this.loading = true;

    this.dataSubscriber$.add(
      this.financialDashboardService.getFinancialDashboardChartData(startDate, endDate).subscribe({
        next: (response) => {
          this.loading = false;

          if (response.success && response.data) {
            this.summary = response.data.summary;
            this.fundUsageIndicators = response.data.fundUsageIndicators || [];

            this.updateChart(
              response.data.labels,
              response.data.wageWithdrawalData,
              response.data.walletWithdrawalData,
              response.data.incomeData,
              response.data.totalFundLimitData,
              response.data.startDate,
              response.data.endDate
            );
          } else {
            this.error = response.message || 'No data available for the specified period.';
            this.toastrService.warning(this.error, 'Data Load Warning');

            if (this.chart) {
              this.chart.destroy();
              this.chart = null;
            }
          }
        },
        error: (err) => {
          this.loading = false;
          this.error = err.error?.message || err.message || 'Failed to load dashboard data';
          console.error(err);
          this.toastrService.error(
            `${this.error} Summary figures may be incorrect due to an invalid filter range.`,
            'Data Load Error'
          );

          if (this.chart) {
            this.chart.destroy();
            this.chart = null;
          }
        }
      })
    )
  }

  private updateChart(
    labels: string[],
    wageData: number[],
    walletData: number[],
    incomeData: number[],
    fundLimitData: number[],
    startDate: string,
    endDate: string
  ): void {
    if (this.chart) {
      // Update existing chart
      this.chart.data.labels = labels;
      this.chart.data.datasets[0].data = wageData;
      this.chart.data.datasets[1].data = walletData;
      this.chart.data.datasets[2].data = incomeData;
      this.chart.data.datasets[3].data = fundLimitData;

      // Update title with date range
      if (this.chart.options.plugins?.title) {
        this.chart.options.plugins.title.text = `Financial Dashboard (${this.datePipe.transform(startDate, 'yyyy-MM-dd')} to ${this.datePipe.transform(endDate, 'yyyy-MM-dd')})`;
      }

      this.chart.update();
    } else {
      // Create new chart
      this.createChart(labels, wageData, walletData, incomeData, fundLimitData, startDate, endDate);
    }
  }

  private createChart(
    labels: string[],
    wageData: number[],
    walletData: number[],
    incomeData: number[],
    fundLimitData: number[],
    startDate: string,
    endDate: string
  ): void {
    const config: any = {
      type: 'line',
      data: {
        labels: labels,
        datasets: [
          {
            label: 'Cumulative Wage Withdrawal',
            data: wageData,
            borderColor: 'rgb(255, 99, 132)',
            backgroundColor: 'rgba(255, 99, 132, 0.1)',
            borderWidth: 2,
            tension: 0.4,
            fill: false,
            pointRadius: 3,
            pointHoverRadius: 5
          },
          {
            label: 'Cumulative Wallet Withdrawal',
            data: walletData,
            borderColor: 'rgb(54, 162, 235)',
            backgroundColor: 'rgba(54, 162, 235, 0.1)',
            borderWidth: 2,
            tension: 0.4,
            fill: false,
            pointRadius: 3,
            pointHoverRadius: 5
          },
          {
            label: 'Cumulative Income',
            data: incomeData,
            borderColor: 'rgb(75, 192, 192)',
            backgroundColor: 'rgba(75, 192, 192, 0.1)',
            borderWidth: 2,
            tension: 0.4,
            fill: false,
            pointRadius: 3,
            pointHoverRadius: 5
          },
          {
            label: 'Total Fund Limit',
            data: fundLimitData,
            borderColor: 'rgb(255, 159, 64)',
            backgroundColor: 'rgba(255, 159, 64, 0.1)',
            borderWidth: 3,
            borderDash: [10, 5],
            tension: 0,
            fill: false,
            pointRadius: 0,
            pointHoverRadius: 0
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          mode: 'index',
          intersect: false,
        },
        plugins: {
          title: {
            display: true,
            text: `Financial Dashboard (${this.datePipe.transform(startDate, 'yyyy-MM-dd')} to ${this.datePipe.transform(endDate, 'yyyy-MM-dd')})`,
            font: {
              size: 18,
              weight: 'bold'
            },
            padding: { bottom: 20 }
          },
          legend: {
            display: true,
            position: 'top',
            labels: {
              font: { size: 12 },
              padding: 15,
              usePointStyle: true
            }
          },
          tooltip: {
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            padding: 12,
            titleFont: { size: 14 },
            bodyFont: { size: 13 },
            callbacks: {
              label: (context: any) => {
                let label = context.dataset.label || '';
                if (label) {
                  label += ': ';
                }
                if (context.parsed.y !== null) {
                  label += new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'LKR'
                  }).format(context.parsed.y);
                }
                return label;
              }
            }
          }
        },
        scales: {
          x: {
            display: true,
            title: {
              display: true,
              text: 'Date',
              font: {
                size: 14,
                weight: 'bold'
              }
            },
            ticks: {
              font: { size: 11 },
              maxRotation: 45,
              minRotation: 45
            },
            grid: {
              display: true,
              color: 'rgba(0, 0, 0, 0.05)'
            }
          },
          y: {
            display: true,
            title: {
              display: true,
              text: 'Amount (LKR)',
              font: {
                size: 14,
                weight: 'bold'
              }
            },
            ticks: {
              font: { size: 12 },
              callback: function (value: any) {
                return new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: 'LKR',
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0
                }).format(value);
              }
            },
            grid: {
              display: true,
              color: 'rgba(0, 0, 0, 0.1)'
            }
          }
        }
      }
    };

    this.chart = new Chart(this.chartCanvas.nativeElement, config);
  }

  // Helper methods for template
  formatCurrency(value: number | null | undefined): string {
    if (value === null || value === undefined) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'LKR'
    }).format(value);
  }

  formatPercentage(value: number | null | undefined): string {
    if (value === null || value === undefined) return '0.00%';
    return `${value.toFixed(2)}%`;
  }

  get hasFundWarnings(): boolean {
    return this.fundUsageIndicators && this.fundUsageIndicators.length > 0;
  }

  get fundWarningsCount(): number {
    return this.fundUsageIndicators.length;
  }

  get isFundBeingUsed(): boolean {
    return this.summary?.isFundBeingUsed || false;
  }

  ngOnDestroy(): void {
    this.chart?.destroy();
    this.dataSubscriber$.unsubscribe();
  }

}
